<template>
  <b-modal
    id="schedule-modal"
    header-class="border-bottom-0 pb-0"
    body-class="schedule-modal-body"
    dialog-class="schedule-modal-dialog"
    content-class="schedule-modal-content border-0"
    hide-footer
    centered
    @hidden="toggleMoreDates(false)"
    @shown="initializeData"
  >
    <b-container v-if="settings">
      <div v-if="isMoreDatesToggled">
        <div class="font-weight-bold mb-3">
          Select
          {{ logisticType === "scheduled_delivery" ? "Delivery" : "Pickup" }}
          Date
        </div>
        <datepicker v-model="selectedDate" :config="dateConfig" />
      </div>
      <div v-else>
        <b-row class="mb-4">
          <b-col>
            <span class="h3 mr-3 align-middle">Pick a date</span>
            <b-img
              v-if="durationInDays > maxInitialDays"
              :src="require('@/assets/images/edit.png')"
              alt="Edit"
              class="cursor-pointer"
              @click="toggleMoreDates(true)"
            />
          </b-col>
        </b-row>
        <div id="tabs" v-dragscroll>
          <div
            class="tab"
            v-for="(item, key) in settings.initial_dates"
            :key="key"
            @click="selectDate(item)"
          >
            <div
              class="pills"
              :class="{
                selected: selectedDate === item.fullDate,
                disabled: item.remaining_orders_accepted === 0 || item.closed
              }"
              :style="[
                selectedDate === item.fullDate
                  ? {
                      backgroundColor: `${design.main_color} !important`,
                      color: `${design.button_text_color} !important`
                    }
                  : {}
              ]"
            >
              <div class="top">
                <template v-if="moment(item.fullDate).isSame(moment(), 'day')">
                  Today
                </template>
                <template
                  v-else-if="
                    moment(item.fullDate).isSame(moment().add(1, 'days'), 'day')
                  "
                >
                  Tomorrow
                </template>
                <template v-else>
                  {{ item.monthShortName }}
                </template>
              </div>
              <div class="middle">{{ item.dayNumber }}</div>
              <div class="bottom">{{ item.dayShortName }}</div>
            </div>
            <div
              class="slot-info"
              v-if="
                item.closed ||
                  typeof item.remaining_orders_accepted === 'number'
              "
            >
              <template v-if="item.closed"> Closed </template>
              <template v-else>
                {{
                  item.remaining_orders_accepted === 0
                    ? "Full"
                    : `${item.remaining_orders_accepted} slot${
                        item.remaining_orders_accepted > 1 ? "s" : ""
                      } left`
                }}
              </template>
            </div>
          </div>
        </div>
      </div>
      <b-row class="mt-4">
        <b-col>
          <b-form-group
            :label="
              logisticType === 'scheduled_delivery'
                ? 'Delivery Time'
                : 'Pickup Time'
            "
            label-class="font-weight-bold"
          >
            <b-dropdown
              class="dropdown-select"
              variant="link"
              :disabled="isCutOff"
              no-caret
            >
              <template slot="button-content">
                <div class="d-flex justify-content-between">
                  <div>
                    {{ selectedTime }}
                  </div>
                  <img src="@/assets/images/dropdown.svg" alt="Dropdown" />
                </div>
              </template>
              <b-dropdown-item
                v-for="(time, key) in timeOptions"
                :key="key"
                @click="selectedTime = time"
              >
                {{ time }}
              </b-dropdown-item>
            </b-dropdown>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row v-if="isCutOff">
        <b-col>
          <div class="text-danger text-center">Order cut off already.</div>
          <div class="text-danger text-center">
            Please select another date and time.
          </div>
        </b-col>
      </b-row>
      <b-row class="mt-3">
        <b-col>
          <b-button
            variant="primary"
            block
            pill
            @click="selectSchedule"
            :disabled="isCutOff"
            :style="{
              backgroundColor: `${design.main_color} !important`,
              color: `${design.button_text_color} !important`
            }"
          >
            SELECT THIS SCHEDULE
          </b-button>
        </b-col>
      </b-row>
    </b-container>
  </b-modal>
</template>

<script>
import moment from "moment";
import { dragscroll } from "vue-dragscroll";
import Datepicker from "vue-bulma-datepicker";
import { mapState, mapGetters } from "vuex";

export default {
  directives: {
    dragscroll
  },
  components: {
    Datepicker
  },
  props: ["brandBranch"],
  data() {
    return {
      moment,
      timeOptions: [],
      selectedTime: null,
      selectedDate: null,
      dateConfig: {
        dateFormat: "Y-m-d",
        inline: true,
        enable: [],
        disable: [],
        minDate: null,
        maxDate: null
      },
      isMoreDatesToggled: false,
      settingsKeys: {
        scheduled_delivery: "delivery_settings",
        scheduled_pickup: "pickup_settings"
      },
      settings: null,
      cutOffTime: null,
      durationInDays: 0,
      maxInitialDays: 14
    };
  },
  computed: {
    ...mapState("merchant", ["customerPortalToken"]),
    ...mapState("estore", ["loading", "updateDeliveryErrors"]),
    ...mapGetters("estore", ["design"]),
    isToday() {
      return moment(this.selectedDate).isSame(moment(), "day");
    },
    isCutOff() {
      return (
        this.isToday &&
        this.cutOffTime &&
        moment(moment(), "HH:mm") >= moment(this.cutOffTime, "HH:mm")
      );
    }
  },
  methods: {
    toggleMoreDates(isToggled) {
      this.isMoreDatesToggled = isToggled;

      if (isToggled) this.selectedDate = this.dateConfig.minDate;
    },
    initializeData() {
      const style = document.createElement("style");
      style.innerHTML = `.flatpickr-day.selected, .flatpickr-day.selected:hover {background: ${this.design.main_color}; border: 0;}`;
      document.body.appendChild(style);

      this.branchDetails = this.brandBranch.details;
      this.logisticType = this.brandBranch.details.selectedLogisticType;

      const settings =
        this.branchDetails &&
        this.branchDetails[this.settingsKeys[this.logisticType]];
      const minDateMoment = moment()
        .add(settings.minimum_lead_time_value, settings.minimum_lead_time)
        .add(settings.initial_dates.length, "days");
      const maxDateMoment = moment()
        .add(settings.minimum_lead_time_value, settings.minimum_lead_time)
        .add(settings.duration_lead_time_value, settings.duration_lead_time)
        .subtract(1, "days");
      const savedSchedule = this.brandBranch.scheduledDate;
      const minDate = minDateMoment.format("YYYY-MM-DD");
      const maxDate = maxDateMoment.format("YYYY-MM-DD");

      this.durationInDays = moment
        .duration(
          settings.duration_lead_time_value,
          settings.duration_lead_time
        )
        .asDays();
      settings.initial_dates.map(item => {
        item.fullDate = moment(item.date).format("YYYY-MM-DD");
        item.monthShortName = moment(item.date).format("MMM");
        item.dayNumber = moment(item.date).format("D");
        item.dayShortName = moment(item.date).format("ddd");
        item.dayFullNameLowerCase = moment(item.date)
          .format("dddd")
          .toLowerCase();
        item.closed = false;

        const storeHours = this.branchDetails.store_hours.find(storeHours => {
          return storeHours.day === item.dayFullNameLowerCase;
        });
        const preOrderSched =
          settings.pre_order_schedules &&
          settings.pre_order_schedules[item.dayFullNameLowerCase]
            ? settings.pre_order_schedules[item.dayFullNameLowerCase]
            : null;

        if (
          ("temporary_closed" in storeHours && storeHours.temporary_closed) ||
          storeHours.hide ||
          (preOrderSched && !preOrderSched.enable)
        )
          item.closed = true;
      });
      settings.store_hours = this.branchDetails.store_hours;
      this.dateConfig.minDate = minDate;
      this.dateConfig.maxDate = maxDate;

      while (minDateMoment < maxDateMoment) {
        const dayFullNameLowerCase = minDateMoment.format("dddd").toLowerCase();
        const storeHours = this.branchDetails.store_hours.find(storeHours => {
          return storeHours.day === dayFullNameLowerCase;
        });
        const preOrderSched =
          settings.pre_order_schedules &&
          settings.pre_order_schedules[dayFullNameLowerCase]
            ? settings.pre_order_schedules[dayFullNameLowerCase]
            : null;
        const currentDate = minDateMoment.format("YYYY-MM-DD");

        if (
          ("temporary_closed" in storeHours && storeHours.temporary_closed) ||
          storeHours.hide ||
          (preOrderSched && !preOrderSched.enable)
        )
          this.dateConfig.disable.push(currentDate);

        minDateMoment.add(1, "days");
      }

      this.settings = settings;
      this.selectedDate = savedSchedule
        ? moment(this.brandBranch.scheduledDate).format("YYYY-MM-DD")
        : moment()
            .add(settings.minimum_lead_time_value, settings.minimum_lead_time)
            .format("YYYY-MM-DD");

      this.generateTimeSlots();

      if (savedSchedule) this.selectedTime = moment(savedSchedule).format("LT");
    },
    selectDate(item) {
      if (item.remaining_orders_accepted === 0 || item.closed) return;

      this.selectedDate = item.fullDate;
    },
    async selectSchedule() {
      this.$root.$emit("bv::hide::modal", "schedule-modal");
      await this.updateLogisticType();
    },
    generateTimeSlots() {
      this.cutOffTime = null;
      let startTime = "";
      let endTime = "";
      const settings = this.settings;
      const selectedDay = moment(this.selectedDate)
        .format("dddd")
        .toLowerCase();
      const { opening, closing, cut_off_time } = settings.store_hours.find(
        storeHours => {
          return storeHours.day === selectedDay;
        }
      );

      startTime = opening;
      endTime = closing;

      if (settings.schedule_type === "days_and_time") {
        const { start, end } = settings.pre_order_schedules[selectedDay];
        startTime = start;
        endTime = end;
      }

      if (cut_off_time) {
        this.cutOffTime = cut_off_time;
        endTime = cut_off_time;
      }

      let startTimeMoment = moment(startTime, "HH:mm");
      const endTimeMoment = moment(endTime, "HH:mm");

      if (
        this.isToday &&
        (settings.minimum_lead_time === "hours" ||
          settings.minimum_lead_time === "minutes")
      ) {
        const minLeadTimeMoment = moment().add(
          settings.minimum_lead_time_value,
          settings.minimum_lead_time
        );

        if (minLeadTimeMoment < endTimeMoment)
          startTimeMoment = minLeadTimeMoment;
        else this.cutOffTime = endTimeMoment.format("HH:mm");
      }

      this.timeOptions = [];

      if (this.isToday && moment(moment(), "HH:mm") >= endTimeMoment) {
        this.selectedTime = null;

        return;
      }

      while (startTimeMoment <= endTimeMoment) {
        this.timeOptions.push(startTimeMoment.format("LT"));
        startTimeMoment.add(30, "minutes");
      }

      if (this.timeOptions.length) this.selectedTime = this.timeOptions[0];
    },
    async updateLogisticType() {
      const { checkout_uuid } = this.$route.params;
      const request = {
        checkout_uuid: checkout_uuid,
        header: {
          "X-Merchant-Token": this.customerPortalToken
        },
        payload: {
          branch_uuid: this.brandBranch.branch_uuid,
          branch_name: this.brandBranch.branch,
          brand_uuid: this.brandBranch.brand_uuid,
          brand_name: this.brandBranch.brand,
          delivery_type: this.branchDetails.logisticTypeOptions[
            this.logisticType
          ].name,
          delivery_datetime: `${this.selectedDate} ${moment(this.selectedTime, [
            "h:mm A"
          ]).format("HH:mm:ss")}`
        }
      };

      await this.$store.dispatch("estore/updateDelivery", request);

      if (
        this.updateDeliveryErrors &&
        this.updateDeliveryErrors.constructor === Object &&
        Object.keys(this.updateDeliveryErrors).length
      ) {
        const error = this.updateDeliveryErrors[
          Object.keys(this.updateDeliveryErrors)[0]
        ];
        const message = Array.isArray(error) && error.length ? error[0] : error;

        this.$root.$emit("showPromptModal", {
          message,
          buttonText: "CLOSE"
        });
      } else {
        await this.$parent.viewCart();
        await this.$parent.formatData();
      }
    }
  },
  watch: {
    selectedDate(value) {
      this.generateTimeSlots(value);
    }
  }
};
</script>

<style lang="scss">
#schedule-modal {
  .schedule-modal-body {
    padding-top: 0;
    padding-left: 0.3rem;
    padding-right: 0.3rem;
  }
  .schedule-modal-dialog {
    max-width: 440px !important;
  }
  .schedule-modal-content {
    border-radius: 15px;
    padding-bottom: 50px;
  }
  #tabs {
    display: flex;
    flex: 1;
    flex-wrap: nowrap;
    overflow: auto;
    -ms-overflow-style: none; /* Hide scrollbar for IE and Edge */
    scrollbar-width: none; /* Hide scrollbar for Firefox */
    &::-webkit-scrollbar {
      display: none; /* Hide scrollbar for Chrome */
    }
  }
  .tab {
    flex-shrink: 0;
    flex-grow: 1;
    padding-right: 10px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .pills {
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;
    align-items: center;
    width: 71px;
    height: 88px;
    .top,
    .bottom {
      font-size: 12px;
      font-weight: 500;
    }
    .middle {
      font-size: 22px;
      font-weight: 700;
    }
    & > div {
      cursor: pointer;
    }
    &.selected {
      background: #ff8a00;
      color: #ffffff;
      border-radius: 10px;
    }
    &.disabled {
      color: #c4c6c8 !important;
      background: transparent !important;
      & > div {
        cursor: not-allowed;
      }
    }
  }
  .slot-info {
    background: #fff1f1;
    border-radius: 24px;
    color: #ff2d2d;
    font-weight: 500;
    font-size: 12px;
    text-align: center;
    margin-top: 7px;
    width: 100%;
    padding: 5px;
  }
  .flatpickr-input {
    display: none;
  }
  .flatpickr-calendar {
    box-shadow: none;
  }
  .flatpickr-month {
    border-top: 1px solid #e9ebed;
    border-bottom: 1px solid #e9ebed;
    padding-bottom: 40px;
    margin-bottom: 5px;
  }
  .flatpickr-calendar.open,
  .flatpickr-calendar.inline,
  .flatpickr-month,
  .flatpickr-rContainer,
  .flatpickr-weekdays,
  .dayContainer,
  .flatpickr-days,
  .flatpickr-day {
    width: 100%;
    max-width: 100%;
  }
  .dropdown-menu.show {
    max-height: 130px;
    overflow: auto;
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
    &::-webkit-scrollbar {
      display: none;
    }
  }
  .close {
    outline: 0;
  }
}
</style>
